<script setup>
import { computed, ref, watch } from 'vue';
import {
  addDays,
  addHours,
  endOfMonth,
  format,
  isBefore,
  isSameDay,
  isSameMonth,
  setHours,
  setMinutes,
  setSeconds,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
  addMonths,
} from 'date-fns';
import Button from 'dashboard/components-next/button/Button.vue';
import NativeSelect from 'dashboard/components-next/native-select/NativeSelect.vue';

const props = defineProps({
  initialValue: {
    type: Date,
    default: () => addHours(new Date(), 1),
  },
});

const emit = defineEmits(['apply', 'cancel']);

const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const roundToNextFiveMinutes = date => {
  const roundedDate = new Date(date);
  const currentMinutes = roundedDate.getMinutes();

  roundedDate.setSeconds(0, 0);
  roundedDate.setMinutes(Math.ceil(currentMinutes / 5) * 5);

  return roundedDate;
};

const initialDateTime = roundToNextFiveMinutes(props.initialValue);
const selectedDate = ref(initialDateTime);
const selectedTime = ref(format(initialDateTime, 'HH:mm'));
const visibleMonth = ref(startOfMonth(initialDateTime));

const minimumAllowedDateTime = computed(() => addHours(new Date(), 1));
const minimumAllowedDay = computed(() =>
  startOfDay(minimumAllowedDateTime.value)
);

const buildDateTimeFromSelection = (date, time) => {
  const [hours, minutes] = time.split(':').map(Number);
  return setSeconds(setMinutes(setHours(new Date(date), hours), minutes), 0);
};

const calendarDays = computed(() => {
  const firstVisibleDay = startOfWeek(startOfMonth(visibleMonth.value), {
    weekStartsOn: 0,
  });

  return Array.from({ length: 42 }, (_, index) =>
    addDays(firstVisibleDay, index)
  );
});

const canGoToPreviousMonth = computed(() => {
  const previousMonth = subMonths(visibleMonth.value, 1);
  return !isBefore(endOfMonth(previousMonth), minimumAllowedDay.value);
});

const timeOptions = computed(() => {
  const options = [];
  const baseDate = selectedDate.value || initialDateTime;
  const minAllowed = minimumAllowedDateTime.value;

  for (let hour = 0; hour < 24; hour += 1) {
    for (let minute = 0; minute < 60; minute += 5) {
      const value = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      const dateTime = buildDateTimeFromSelection(baseDate, value);

      if (!isSameDay(baseDate, minAllowed) || dateTime >= minAllowed) {
        options.push({
          value,
          label: format(dateTime, 'h:mm a'),
        });
      }
    }
  }

  return options;
});

const isDisabledDay = day => isBefore(startOfDay(day), minimumAllowedDay.value);

const isSelectedDay = day =>
  selectedDate.value &&
  isSameDay(startOfDay(day), startOfDay(selectedDate.value));

const selectDate = day => {
  if (isDisabledDay(day)) return;
  selectedDate.value = day;
};

const goToPreviousMonth = () => {
  if (!canGoToPreviousMonth.value) return;
  visibleMonth.value = subMonths(visibleMonth.value, 1);
};

const goToNextMonth = () => {
  visibleMonth.value = addMonths(visibleMonth.value, 1);
};

watch(selectedDate, date => {
  if (date) {
    visibleMonth.value = startOfMonth(date);
  }
});

watch(
  timeOptions,
  options => {
    if (!options.some(option => option.value === selectedTime.value)) {
      selectedTime.value = options[0]?.value || '00:00';
    }
  },
  { immediate: true }
);

const onApply = () => {
  if (!selectedDate.value || !selectedTime.value) return;
  emit(
    'apply',
    buildDateTimeFromSelection(selectedDate.value, selectedTime.value)
  );
};
</script>

<template>
  <div
    class="min-w-[336px] overflow-hidden rounded-2xl border border-n-weak bg-n-surface-1 shadow-xl"
  >
    <div class="flex items-center gap-2 border-b border-n-weak px-4 py-3">
      <span class="i-lucide-calendar-days size-4 text-n-slate-11" />
      <span class="text-base font-medium text-n-slate-12">
        {{ $t('CONVERSATION.CUSTOM_SNOOZE.TITLE') }}
      </span>
    </div>

    <div class="px-4 py-3">
      <div class="mb-3 flex items-center justify-between">
        <button
          type="button"
          class="inline-flex size-8 items-center justify-center rounded-lg text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 disabled:cursor-not-allowed disabled:opacity-40"
          :disabled="!canGoToPreviousMonth"
          @click="goToPreviousMonth"
        >
          <span class="i-lucide-chevron-left size-4" />
        </button>

        <span class="text-sm font-semibold text-n-slate-12">
          {{ format(visibleMonth, 'MMMM yyyy') }}
        </span>

        <button
          type="button"
          class="inline-flex size-8 items-center justify-center rounded-lg text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
          @click="goToNextMonth"
        >
          <span class="i-lucide-chevron-right size-4" />
        </button>
      </div>

      <div class="mb-2 grid grid-cols-7 gap-1">
        <span
          v-for="day in WEEK_DAYS"
          :key="day"
          class="flex h-8 items-center justify-center text-xs font-medium text-n-slate-10"
        >
          {{ day }}
        </span>
      </div>

      <div class="grid grid-cols-7 gap-1">
        <button
          v-for="day in calendarDays"
          :key="day.toISOString()"
          type="button"
          class="flex h-9 items-center justify-center rounded-lg text-sm transition-colors"
          :class="[
            isSelectedDay(day)
              ? 'bg-n-brand text-white hover:bg-n-brand'
              : isDisabledDay(day)
                ? 'cursor-not-allowed text-n-slate-8'
                : isSameMonth(day, visibleMonth)
                  ? 'text-n-slate-12 hover:bg-n-alpha-2'
                  : 'text-n-slate-10 hover:bg-n-alpha-2',
            !isSelectedDay(day) && isSameDay(day, new Date())
              ? 'font-semibold outline outline-1 -outline-offset-1 outline-n-brand/30'
              : '',
          ]"
          @click="selectDate(day)"
        >
          {{ format(day, 'd') }}
        </button>
      </div>
    </div>

    <div class="border-t border-n-weak px-4 py-3">
      <div class="mb-3 flex items-center gap-3">
        <span class="text-sm text-n-slate-11">
          {{ $t('CAMPAIGN.FORM.TIME') }}
        </span>
        <div class="w-[132px]">
          <NativeSelect v-model="selectedTime">
            <option
              v-for="option in timeOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </NativeSelect>
        </div>
      </div>

      <div class="flex items-center justify-end gap-2">
        <Button
          faded
          slate
          :label="$t('CONVERSATION.CUSTOM_SNOOZE.CANCEL')"
          @click="emit('cancel')"
        />
        <Button
          :label="$t('CONVERSATION.CUSTOM_SNOOZE.APPLY')"
          @click="onApply"
        />
      </div>
    </div>
  </div>
</template>
